import { MonitorParameter } from '@/global/interface/monitor-object/monitor-parameter'
import './index.less'
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { LineChart } from '@/global/components/line-chart';
import { queryInfluxDataList } from '@/api/influx/influx';
import { TimeValue } from '@/global/interface/time-value';
import TnMessage from '@/global/components/message';
import { Skeleton } from 'antd';
import { Transfer2DisplayScale } from '@/global/utils/unit-type';
import { MONITORING_DATA_TYPE } from '@/enums/monitoring-data-type';
import LoadDisplacementChart from '../../load-displacement';
import axios from 'axios';
import { getDateName } from '@/global/utils/date-utils';

interface Iprops {
    monitorParameters: MonitorParameter[]
}

export default function MonitorParameterHistoryPanel(props: Iprops) {
    const { monitorParameters } = props;
    const [activedParam, setActiveParam] = useState<MonitorParameter>();  // 选中的参数
    const [dataList, setDataList] = useState<number[][]>([]) // 数据曲线
    const [loading, setLoading] = useState<boolean>(false) // 曲线loading
    const intervalRef = useRef<NodeJS.Timeout>(null); // 定时器

    const [neeLoadDisp, setNeedLoadDisp] = useState<boolean>(false); // 是否需要展示荷载位移曲线
    const [showLoadDisp, setShowLoadDisp] = useState<boolean>(false); // 当前是否激活荷载位移曲线
    const [dataPoints, setDataPoints] = useState<number[][]>([]); // 组织好的荷载位移数据点
    const [dispParam, setDispParam] = useState<MonitorParameter>();
    const [loadParam, setLoadParam] = useState<MonitorParameter>();

    const [endTime, setEndTime] = useState<number>(null)


    useEffect(() => {
        if (monitorParameters && monitorParameters.length > 0) {
            if (!activedParam) {
                // 默认选中第一个参数
                setActiveParam(monitorParameters[0]);
            } else {
                let item = monitorParameters.find(item => item.id == activedParam.id)
                if (item) {
                    setActiveParam({ ...item })
                }
            }
        }
    }, [monitorParameters])

    useEffect(() => {
        // 判断是否需要显示荷载位移曲线
        let wpres = monitorParameters.find(item => item.symbol == "wpres");
        let deform = monitorParameters.find(item => item.symbol == "deform");
        if (wpres == null || deform == null) {
            setNeedLoadDisp(false);
            return;
        }
        setNeedLoadDisp(true);
        setDispParam(deform);
        setLoadParam(wpres);
        // 组织荷载位移曲线数据
        generatePoints(wpres, deform);
        const interval = setInterval(() => {
            generatePoints(wpres, deform);
        }, 3000)

        return () => {
            clearInterval(interval)
        }
    }, [monitorParameters])

    // 构建荷载位移的离散点
    const generatePoints = (wpres: MonitorParameter, deform: MonitorParameter) => {
        let queryDeform = queryInfluxDataList(deform.id, deform.symbol, MONITORING_DATA_TYPE.MONITOR_OBJ, { limit: 50 });
        let queryWpres = queryInfluxDataList(wpres.id, wpres.symbol, MONITORING_DATA_TYPE.MONITOR_OBJ, { limit: 50 });
        axios.all([queryDeform, queryWpres]).then(res => {
            let deformList: TimeValue[] = res[0].data || [];
            let wpresList: TimeValue[] = res[1].data || [];

            let points: number[][] = [];
            for (let defPoint of deformList) {
                let time = defPoint.time;
                let wpresPoints = wpresList.filter(item => item.time <= time); // 默认从小到大时间戳
                if (wpresPoints == null || wpresPoints.length == 0) continue;
                let wpresPoint = wpresPoints[wpresPoints.length - 1];

                let point = [defPoint.value * Transfer2DisplayScale(deform.unit), wpresPoint.value * Transfer2DisplayScale(wpres.unit)];
                points.push(point);
            }
            setDataPoints(points);
        })
    }


    useEffect(() => {
        if (!activedParam) return;
        generateData(activedParam);
        const interval = setInterval(() => {
            generateData(activedParam);
        }, 3000);
        return () => {
            clearInterval(interval)
        }
    }, [activedParam, endTime])

    // 查询监测参数曲线图
    const generateData = (param: MonitorParameter) => {
        // 当天的开始时间和当前时间
        const startOfDay = new Date(new Date().setHours(0, 0, 0, 0));
        const now = new Date();
        queryInfluxDataList(param.id, param.symbol, MONITORING_DATA_TYPE.MONITOR_OBJ, {
            limit: 50,
            endTime: endTime
        }).then(res => {
            let valueList: TimeValue[] = res.data || []
            // 组合成数据，展示显示单位下的值
            let scale = Transfer2DisplayScale(activedParam.unit);
            let data = []
            for (let i = 0; i < valueList?.length; i++) {
                data.push([valueList[i].time, (valueList[i].value * scale)])
                // data.push([valueList[i].time, parseFloat((valueList[i].value * scale).toFixed(3))])
            }
            setDataList(data)
        }).catch(() => {
            TnMessage.error("获取历史数据异常")
        }).finally(() => {
            setLoading(false);
        })
    }

    const getChartAxisXLabel = (value: number) => {
        const date = new Date(Number(value));
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();
        if (date.getTime() < today) {
            return `${date.getMonth() + 1}-${date.getDate()} ${hours}:${minutes}`
        }
        return `${hours}:${minutes}`;
    }

    return (
        <div className="monitor-object-param-history-panel">
            <div className="tabs">
                {monitorParameters.map(param => (
                    <div className={classNames({
                        "tab": true,
                        "active": param.id == activedParam?.id
                    })} onClick={() => {
                        setActiveParam(param)
                        setShowLoadDisp(false)
                    }}>
                        <div className="text">{param.encoding}{param.name}</div>
                    </div>
                ))}
                {neeLoadDisp && (
                    <div className={classNames({
                        "tab": true,
                        "active": showLoadDisp
                    })} onClick={() => {
                        setShowLoadDisp(true)
                        setActiveParam(null)
                    }}>
                        <div className="text">荷载位移曲线</div>
                    </div>
                )}
            </div>
            <div className="data">
                {showLoadDisp && (
                    <LoadDisplacementChart
                        points={dataPoints}
                        t2={dispParam.t2 * Transfer2DisplayScale(dispParam.unit)}
                        t3={dispParam.t3 * Transfer2DisplayScale(dispParam.unit)}
                        t4={dispParam.t4 * Transfer2DisplayScale(dispParam.unit)}
                        xUnit={dispParam.unit}
                        yUnit={loadParam.unit}
                    />
                )}
                {!showLoadDisp && (
                    <LineChart
                        chartValue={getDateName(new Date().getTime())}
                        lineData={dataList || []}
                        getAxisXLabel={getChartAxisXLabel}
                        xLabel="时间"
                        yLabel={activedParam?.unit}
                        toolTipXLabel="时间"
                        toolTipYLabel={activedParam?.name}
                        tLine_1={activedParam?.t2 * Transfer2DisplayScale(activedParam?.unit)}
                        tLine_2={activedParam?.t3 * Transfer2DisplayScale(activedParam?.unit)}
                        tLine_3={activedParam?.t4 * Transfer2DisplayScale(activedParam?.unit)}
                        onChangeEndTime={(e) => { setEndTime(e) }}
                    />
                )}

            </div>
        </div>
    )
}